import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { IGridOptions, ICommandColumnOptions, IFieldColumnOptions, IGridCommand, GridColumnSortState as GridColumnSortState } from '../../models';
import { ISimpleFilter, IGroup, IGroupQueryResult, IResolveCommandModelEvent } from '@poweredsoft/data';
import { IPopup, FuiModalService, ModalTemplate, TemplateModalConfig } from 'ngx-fomantic-ui';
import { AlertFacadeService } from '../../../alert-facade-service/services/alert-facade.service';
import { CommandModal } from '../../../command-modal/components/command-modal/CommandModal';
import { IDynamicComponent } from '../../../dynamic-component/IDynamicComponent';
import { Subscription, ObjectUnsubscribedError } from 'rxjs';

@Component({
  selector: 'ps-data-grid',
  templateUrl: './data-grid.component.html',
  styleUrls: ['./data-grid.component.css']
})
export class DataGridComponent implements OnInit, OnDestroy {
  @Input() options: IGridOptions;

  @ViewChild('deleteModalTemplate', { static: true })
  public deleteModalTemplate: ModalTemplate<any, string, string>

  selected: any[] = [];
  hasExpandedOnce: any[] = [];
  expanded: any[] = [];
  groups: IFieldColumnOptions[] = [];
  private _pageGuard: boolean = false;
  dataRows: any[] = [];
  currentCommand: IGridCommand = null;
  commandModel: any = null;
  commandComponent: IDynamicComponent;

  createEditableColumn: ICommandColumnOptions = {
    title: '',
    actions: [
      {
        icon: 'icon cancel',
        buttonClass: 'orange',
        tooltip: 'Cancel',
        callback: () => this.clearForm(),
        visible: () => true
      },
      {
        icon: 'icon save',
        buttonClass: 'green',
        tooltip: 'Save',
        callback: () => {
          this.attemptSave();
        },
        visible: () => true
      }
    ]
  };

  // code here to check for custom commands in datasource
  editableColumn: ICommandColumnOptions = {
    title: '',
    actions: [
      {
        icon: 'icon edit',
        tooltip: 'Edit',
        callback: this.editAction.bind(this),
        visible: m => {
          return !this.isRowBeingEdited(m) && this.isEditEnabled;
        }
      },
      {
        icon: 'icon trash',
        buttonClass: 'red',
        tooltip: 'Delete',
        callback: this.deleteAction.bind(this),
        visible: m => {
          return !this.isRowBeingEdited(m) && this.isDeleteEnabled;
        }
      },
      {
        icon: 'icon cancel',
        buttonClass: 'orange',
        tooltip: 'Cancel',
        callback: () => this.clearForm(),
        visible: m => this.isRowBeingEdited(m)
      },
      {
        icon: 'icon save',
        buttonClass: 'green',
        tooltip: 'Save',
        callback: () => {
          this.attemptSave();
        },
        visible: m => this.isRowBeingEdited(m)
      }
    ]
  };

  private _loadingSubscription: Subscription;
  private _dataSubscription: Subscription;
  private _notifyMessageSubscription: Subscription;
  private _validationErrorNotification: Subscription;
  

  constructor(public modalService: FuiModalService, protected alertFacadeService: AlertFacadeService) {
  }

  get filters() { return this.options.dataSource.filters; }
  get currentPage() { return this.options.dataSource.page; }
  get currentResult() { return this.options.dataSource.data; }
  get isCreateEnabled() { return this.options.commands.findIndex(t => t.type == 'create') > -1; }
  get isEditEnabled() { return this.options.commands.findIndex(t => t.type == 'edit') > -1; }
  get isDeleteEnabled() { return this.options.commands.findIndex(t => t.type == 'delete') > -1; }

  ngOnInit() {
    this._loadingSubscription = this.options.dataSource.loading$.subscribe((value) => {
      this.isLoading = value;
    });

    // groups.
    this.groups = this.options.dataSource.groups.map(t => this.fieldColumns.find(c => (c.groupPath || c.field) == t.path)).filter(t => t);

    // subscriptions.
    this._dataSubscription = this.options.dataSource.data$.subscribe(() => this.refreshDataRows());
    this._notifyMessageSubscription = this.options.dataSource.notifyMessage$.subscribe(notifyMessage => {
      this.alertFacadeService.showMessage({
        position: 'bottom right',
        title: 'Notification',
        message: notifyMessage.message,
        type: notifyMessage.type,
        tapToDismiss: true,
        timeOut: notifyMessage.type == "error" ? undefined : 5000
      });
    });

    this._validationErrorNotification = this.options.dataSource.validationError$.subscribe(notification => {
      let message = Object.keys(notification.errors).map(key => notification.errors[key]).reduce((p, c) => p.concat(c), []).join('\n');

      this.alertFacadeService.showMessage({
        position: 'bottom right',
        title: 'Notification',
        message: message,
        type: 'error',
        tapToDismiss: true
      });
    });

    for(let command of this.options.commands) {
      if (command.type != 'custom') continue;
      this.editableColumn.actions.push({
        title: command.title,
        tooltip: command.title,
        callback: (model) => this.startCommand(command.name, model)
      })
    }

    this.options.dataSource.refresh();
  }

  ngOnDestroy() {
    this._loadingSubscription.unsubscribe();
    this._dataSubscription.unsubscribe();
    this._notifyMessageSubscription.unsubscribe();
    this._validationErrorNotification.unsubscribe();
  }

  get groupOrData() {
    if (this.currentResult == null)
      return null;

    if (this.groups.length)
      return this.currentResult.groups;

    return this.currentResult.data;
  }

  get loading$() {
    return this.options.dataSource.loading$;
  }

  isLoading: boolean = false;

  groupColSpan(row: IGroupQueryResult<any>) {
    return this.headerColumnCount - this.groups.findIndex(t => (t.groupPath || t.field) == row.groupPath);
  }

  getGroupOrData(row: IGroupQueryResult<any>) {
    return row.hasSubGroups ? row.subGroups : row.data;
  }

  getGroupTitle(row: IGroupQueryResult<any>) {
    let group = this.groups.find(t => (t.groupPath || t.field) == row.groupPath);
    return group ? group.title : row.groupPath;
  }

  groupsChanged() {
    this.options.dataSource.groups = this.groups.map(t => <IGroup>{
      path: t.groupPath || t.field,
      ascending: true // TODO make it that if you click on the group it changes the direction of the sort :)
    });
  }

  get fieldColumns(): IFieldColumnOptions[] {
    return this.options.columns.filter(t => !t.type || t.type == 'field') as any;
  }

  get groupableColumns() {
    return this.fieldColumns.filter(t => t.groupable !== false);
  }

  groupCount(row: IGroupQueryResult<any>) {
    let index = this.groups.findIndex(t => (t.groupPath || t.field) == row.groupPath);
    return new Array(index);
  }

  get groupable() {
    return this.options.groupable;
  }

  isGroup(data: IGroupQueryResult<any>) {
    return data.hasSubGroups !== undefined;
  }

  refreshRowStates() {

    this.refreshDataRows();
    this.selected = this.getSelectedIfStillPresent();
    this.hasExpandedOnce = [];
    this.expanded = [];
  }

  getSelectedIfStillPresent(): any[] {
    let oldSelected = this.selected.slice(0);
    return this.dataRows.filter(t => oldSelected.findIndex(t2 => this.options.dataSource.resolveIdField(t) == t2) > -1).map(t => this.options.dataSource.resolveIdField(t));
  }

  selectVisibleColumns(popup: IPopup) {
    setTimeout(() => {
      popup.open();
    });
  }

  /*
  selectVisibleColumns() {

    const config = new TemplateModalConfig<any, string, string>(this.modalTemplate);
    config.context = { 
      options: this.options
    };
    config.size = 'mini';

    this.modalService.open(config);
  }*/

  isSelected(model: any) {
      const id = this.options.dataSource.resolveIdField(model);
      return this.selected.findIndex(t => id == t) > -1;
  }

  refreshDataRows() {
    if (this.currentResult == null) {
      this.dataRows = [];
      return;
    }

    if (this.groups.length == 0) {
      this.dataRows = this.currentResult.data;
      return;
    }

    const flattenResults = (root: IGroupQueryResult<any>) => {
      if (root.hasSubGroups)
        return root.subGroups.reduce((p, c) => p.concat(flattenResults(c)), []);
      else
        return root.data;
    };

    this.dataRows = this.currentResult.groups.reduce((p, c) => p.concat(flattenResults(c)), []);
  }

  get allSelected() {
    return this.selected.length == this.dataRows.length;
  }

  selectAllChanged(allSelected) {
    if (allSelected)
      this.selected = this.dataRows.map(t => this.options.dataSource.resolveIdField(t));
    else 
      this.selected = [];
  }

  get grid() {
    return this;
  }

  pageChanged(page) {
    if (this._pageGuard) {
      this._pageGuard = false;
      return;
    }

    this.options.dataSource.page = page;
  }

  get pagingEnabled() { return this.options.dataSource.pageSize ? true : false; }
  get pagingSize() { return this.options.dataSource.pageSize;}
  get numberOfPages() { return this.currentResult ? this.currentResult.numberOfPages : null; }

  get unstackable() {
    return !this.options.stackable;
  }

  get selectable() {
    return this.options.selectable;
  }

  get visibleColumns() {
    return this.options.columns.filter(t => t.visible !== false).sort(t => t.order);
  }

  get hasEditableActionGroupColumn() {
    return this.isEditEnabled || this.isDeleteEnabled || this.options.commands.find((command) => { return command.type == 'custom'; });
  }

  get hasDetailComponent() {
    return this.options.detailComponent ? true : false;
  }

  hasModelExpandedOnce(model: any) {
    return this.hasExpandedOnce.indexOf(model) > -1;
  }

  get isEditing() {
    return this.commandModel ? true : false;
  }

  isRowBeingEdited(model: any) {
    return this.isRowEditing(model) || this.isInlineEditing(model);
  }

  isRowEditing(model: any) {
    return this.options.editableMode == 'row'
      && this.currentCommand
      && (this.currentCommand.type == 'edit' || this.currentCommand.type == 'custom')
      && this.commandModel
      && this.getIdField(model) == this.getIdField(this.commandModel);
  }

  isInlineEditing(model: any) {
    return this.options.editableMode == 'inline'
      && this.currentCommand
      && this.currentCommand.type == 'edit'
      && this.commandModel
      && this.getIdField(model) == this.getIdField(this.commandModel);
  }

  isExpanded(model: any) {
    return this.isRowBeingEdited(model) ? false : this.expanded.indexOf(model) > -1;
  }

  toggleExpanded(model) {
    if (this.expanded.indexOf(model) === -1) {
      this.expanded.push(model);
      if (!this.hasModelExpandedOnce(model))
        this.hasExpandedOnce.push(model);
    }
    else
      this.expanded = this.expanded.filter(t => t != model);
  }

  attemptSave() {

    if (!this.commandModel)
      return;

    this.options.dataSource.executeCommandByName(this.currentCommand.name, this.commandModel)
      .subscribe(
        () => {
          this.clearForm();
          this.options.dataSource.refresh();
        },
        () => {
          // TODO.
        }
      );
  }

  get headerColumnCount() {
    return this.visibleColumns.length + this.groups.length + (this.hasDetailComponent ? 1 : 0) + (this.hasEditableActionGroupColumn ? 1 : 0) + (this.options.selectable ? 1 : 0);
  }

  get detailOffsetColSpan() {
    return this.groups.length + (this.selectable ? 1 : 0);
  }

  get detailColSpan() {
    return this.visibleColumns.length + (this.hasEditableActionGroupColumn ? 1 : 0) + 1;
  }

  createAction() {
    if (!this.isCreateEnabled)
      return;

    const command = this.options.commands.find(t => t.type == 'create');
    if (!command)
      return;

    this.startCommand(command.name);
  }

  protected clearForm() {
    this.currentCommand = null;
    this.commandModel = null;
  }

  protected modalCommand(command: IGridCommand, commandModel: any, isfullScreen?:boolean) {

    const modalConfig = new CommandModal({
      dataSource: this.options.dataSource,
      title: command.title,
      command: command.name,
      commandModel: commandModel,
      component: command.component(commandModel, command)      
    }, isfullScreen);

    this.modalService
        .open(modalConfig)
        .onApprove(_ => { this.refresh(); })
        .onDeny(() => {});
  }  

  protected startCommand(name: string, model: any = null) {
    const command = this.options.commands.find(t => t.name == name);

    if (!command)
      return;

    const modelCopy = JSON.parse(JSON.stringify(model));
    this.options.dataSource.resolveCommandModelByName({
      command: name,
      model: modelCopy
    }).subscribe(commandModel => {
      if (this.options.editableMode == 'popup' && !this.options.popupUseFullScreen)
      {
        this.modalCommand(command, commandModel);
      }
      else if (this.options.editableMode == 'popup' && this.options.popupUseFullScreen)      
      {
        this.modalCommand(command, commandModel, true);
      }
      else if (this.options.editableMode == 'row')
      {
        this.commandModel = commandModel;
        this.currentCommand = command;
        this.commandComponent = command.component(commandModel, command);
      }
      else if (this.options.editableMode == 'inline')
      {
        if (command.type == 'create' || command.type == 'edit')
        {
          this.commandModel = commandModel;
          this.currentCommand = command;
        }
        else if (command.component)
          this.modalCommand(command, commandModel);
      }
    });

    // const mode = this.options.editableMode;

    // if (mode === 'popup') {

    //   let modalConfig = new EditableModal({
    //     title: create ? 'Create' : 'Edit',
    //     mode: create ? 'create' : 'edit',
    //     idField: this.options.idField,
    //     adapter: this.options.adapter,
    //     component: this.options.editable.component,
    //     componentProps: this.options.editable.componentProps,
    //     editableFormModel: formModel
    //   });

    //   this.modalService
    //     .open(modalConfig)
    //     .onApprove(afterSaveModel => {
    //       this.refresh();
    //     })
    //     .onDeny(() => {

    //     });
    // }
    // else if (mode === 'inline') {
    //   this.formModel = formModel;
    //   this.formMode = create ? 'create' : 'edit';
    // }
    // else if (mode === 'row') {
    //   this.formModel = formModel;
    //   this.formMode = create ? 'create' : 'edit';
    // }
  }

  editAction(model: any) {

    const command = this.options.commands.find(t => t.type == 'edit');
    if (command == null)
      return;

    this.startCommand(command.name, model);
  }

  deleteAction(model: any) {

    const command = this.options.commands.find(t => t.type == 'delete');
    if (command == null)
      return;

    const config = new TemplateModalConfig<any, string, string>(this.deleteModalTemplate);

    config.closeResult = "closed!";
    config.context = {};

    this.modalService
      .open(config)
      .onApprove(() => {
        this.options.dataSource.resolveCommandModelByName({
          command: command.name,
          model: model
        }).subscribe(commandModel => {
          this.options.dataSource.executeCommandByName(command.name, commandModel)
            .subscribe(
                res => this.refresh(),
                err => {}
            );
        });
      });
  }

  clearAllFilters(popup: IPopup) {
    popup.close();
    this.options.dataSource.query({
      filters: [],
      page: 1
    });
  }

  getIdField(model: any) {
    return this.options.dataSource.resolveIdField(model);
  }

  matchByIdField(left: any, right: any) {
    return this.getIdField(left) == this.getIdField(right);
  }

  changeRowSelected(model: any, value: boolean) {

      const id = this.getIdField(model);
      const indexOf = this.selected.findIndex(s => id == s);
      if (value && indexOf === -1)
        this.selected.push(id);
      else if (indexOf > -1)
        this.selected = this.selected.filter(s => s != id);
  }

  onColumnSort(column: IFieldColumnOptions) {

    const columnSort = this.options.dataSource.sorts.find(t => t.path == (column.sortPath || column.field));
    if (columnSort)
    {
      if (columnSort.ascending == true)
      {
        columnSort.ascending = false;
        this.options.dataSource.refresh();
      } else {
        const newSorts = this.options.dataSource.sorts.filter(t => t.path != (column.sortPath || column.field));
        this.options.dataSource.query({
          sorts: newSorts,
          page: 1
        });
      }
    }
    else
    {
      this.options.dataSource.sorts.push({
        path: column.sortPath || column.field,
        ascending: true
      });
      this.options.dataSource.page = 1;
    }
  }

  getColumnSortState(column: IFieldColumnOptions) {
    const existing = this.options.dataSource.sorts.find(t => t.path == (column.sortPath || column.field));

    if (!existing)
      return GridColumnSortState.notSorted;

    return existing.ascending === false ? GridColumnSortState.desc : GridColumnSortState.asc;
  }

  getColumnFilter(column: IFieldColumnOptions) {
    return this.filters.find(t => (t as ISimpleFilter).path == (column.filterPath || column.field));
  }

  clearFilter(filter: ISimpleFilter) {
    this.options.dataSource.query({
      filters: this.filters.filter(t => t != filter),
      page: 1
    });
  }

  onColumnFilter(filter: ISimpleFilter) {

    // get the index.
    let index = this.filters.findIndex(t => (t as ISimpleFilter).path == filter.path);
    if (index >= 0)
    {
      this.filters[index] = filter;
    }
    else
    {
      this.filters.push(filter);
    }

    this.options.dataSource.page = 1;
  }

  setCurrentPage(page: number) {

    if (this.currentPage != page)
      this._pageGuard = true;

    this.options.dataSource.page = page;
  }

  refresh() {
    this.options.dataSource.refresh();
  }
}
